<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import AppIcon from '@/components/common/AppIcon.vue'
import { assetUrl } from '@/utils/assets'

/**
 * Tribe crest: a real image on R2 (`PlayerTribe.icon`, resolved server-side by
 * `AssetService::url()`), not an mdi icon name — hence `<img>` and not
 * `AppIcon` for the primary case. Same fallback pattern as `UserAvatar.vue`:
 * `icon` can be `null` (no icon seeded yet) or 404 (asset not uploaded to R2
 * yet), and both degrade to a generic mdi icon instead of a broken image.
 */
const props = withDefaults(
  defineProps<{
    icon?: string | null
    /** Tribe name, used only for the (decorative) title tooltip. */
    name?: string | null
    sizeClass?: string
  }>(),
  { icon: null, name: null, sizeClass: 'h-5 w-5' },
)

/** Some payloads (`getUser`) carry the raw stored path instead of the URL. */
const src = computed(() => assetUrl(props.icon))

const failed = ref(false)
watch(
  () => props.icon,
  () => {
    failed.value = false
  },
)
</script>

<template>
  <span
    class="inline-flex shrink-0 items-center justify-center"
    :class="sizeClass"
    :title="name ?? undefined"
  >
    <img
      v-if="src && !failed"
      :src="src"
      alt=""
      class="h-full w-full object-contain"
      loading="lazy"
      decoding="async"
      @error="failed = true"
    />
    <AppIcon v-else name="mdi-account-group" class="text-abyss-800/60 text-lg" />
  </span>
</template>
